import { FormEvent, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { track } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

const DESCRIPTION =
  "Track and improve how your brand shows up across ChatGPT, Perplexity, Gemini, and Google AI — so you're the name AI recommends."

const TIERS = [
  {
    name: "Snapshot",
    line: "A first-look report on exactly where you appear — and where competitors beat you — across the major AI assistants, for the prompts your buyers actually use.",
  },
  {
    name: "Dashboard",
    line: "Your own AI Visibility dashboard — a real login with ongoing scores and competitor tracking, refreshed continuously. Yours and your team's to check any time.",
  },
  {
    name: "Grow",
    line: "Done-for-you: we improve the authoritative content and signals AI cites — so you don't just measure visibility, you build it.",
  },
]

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "We map your prompts",
    body: "Together we identify the real questions buyers are asking AI in your category — not vanity keywords, the ones that actually drive deals.",
  },
  {
    step: "02",
    title: "We run them across every assistant",
    body: "ChatGPT, Claude, Perplexity, Gemini, Google's AI Overviews. We capture who gets named, who gets cited, and where you sit vs. your competitors.",
  },
  {
    step: "03",
    title: "You get a live dashboard, not a PDF",
    body: "Your scores, rankings, citations, and overnight changes live in your own dashboard — yours to log into between meetings instead of waiting on a one-time report.",
  },
  {
    step: "04",
    title: "We move the needle",
    body: "We build the kind of authoritative, frequently-cited content the models actually draw from — so the next time AI ranks the shortlist, you're on it.",
  },
]

export default function Ai() {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const joined = Boolean(props.flash?.notice)

  const form = useForm({ name: "", email: "", website: "", nickname: "", referrer: "", utm_source: "" })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    form.setData((data) => ({
      ...data,
      referrer: document.referrer || "",
      utm_source: params.get("utm_source") || "",
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    track("AI Waitlist Joined")
    form.post("/ai/waitlist")
  }

  return (
    <MarketingLayout>
      <SeoHead title="AI Visibility" description={DESCRIPTION} noindex />

      {/* Hero + waitlist */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · AI Visibility</Eyebrow>
        <h1 className="display-1 mt-4">When buyers ask AI who to hire, is it you?</h1>
        <p className="mt-5 text-lg text-ink-body">
          Your prospects are asking ChatGPT, Perplexity, and Gemini for
          recommendations right now. If AI doesn't surface you, you lose deals you
          never even see — and SEO can't fix it. FullCast tracks and improves how
          your brand shows up in AI answers, so you're the name they recommend.
        </p>

        <div id="waitlist" className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
          {joined ? (
            <div>
              <h2 className="display-2">You're on the list.</h2>
              <p className="mt-3 text-ink-body">
                {props.flash?.notice} We're onboarding a first group — you'll hear
                from us with a snapshot of where you stand in AI search.
              </p>
            </div>
          ) : (
            <>
              <h2 className="display-2">Get your AI Visibility snapshot</h2>
              <p className="mt-2 text-ink-muted">
                Join the waitlist — we'll send you a snapshot of where you show up
                (and where you don't) across the major AI assistants.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
                <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
                  <label htmlFor="nickname">Leave this field empty</label>
                  <input
                    id="nickname"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.data.nickname}
                    onChange={(e) => form.setData("nickname", e.target.value)}
                  />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="name">Name</label>
                    <Input id="name" autoComplete="name" value={form.data.name} onChange={(e) => form.setData("name", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email">Email</label>
                    <Input id="email" type="email" autoComplete="email" value={form.data.email} onChange={(e) => form.setData("email", e.target.value)} />
                    {errors.email && <p className="text-xs text-danger-display">{errors.email}</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="website">Website or brand</label>
                  <Input id="website" placeholder="acme.com" value={form.data.website} onChange={(e) => form.setData("website", e.target.value)} />
                </div>
                <Button type="submit" size="lg" disabled={form.processing}>
                  {form.processing ? "Joining…" : "Join the waitlist"}
                </Button>
              </form>
            </>
          )}
        </div>
      </Section>

      {/* Dashboard preview */}
      <Section containerClassName="max-w-4xl">
        <Eyebrow>Your AI Visibility portal</Eyebrow>
        <h2 className="display-2 mt-4">A dashboard, not a one-off report</h2>
        <p className="mt-3 text-ink-muted">
          You get your own login — scores, rankings, citations, and overnight changes,
          updated continuously. Check in any time, share it with your team.
        </p>

        <div className="mt-8 overflow-hidden rounded-2xl border border-hairline bg-surface">
          {/* Top bar */}
          <div className="flex items-center justify-between border-b border-hairline px-6 py-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-accent text-xs font-bold text-white">A</span>
              <span className="font-medium text-ink-display">Acme Consulting</span>
            </div>
            <span className="text-sm text-ink-muted">ai.fullcast.co/acme</span>
          </div>

          {/* Score cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 border-b border-hairline">
            {[
              { label: "VISIBILITY", value: "84", change: null },
              { label: "THIS MONTH", value: "+12%", change: "up" },
              { label: "PRESENCE", value: "89", change: null },
              { label: "RANK", value: "#3", change: null },
              { label: "CITATIONS", value: "47", change: null },
            ].map((s) => (
              <div key={s.label} className="border-b border-hairline px-5 py-4 sm:border-b-0 sm:border-r last:border-r-0">
                <p className="text-[10px] font-semibold tracking-wider text-ink-muted">{s.label}</p>
                <p className={`mt-1 text-2xl font-bold ${s.change === "up" ? "text-green-500" : "text-ink-display"}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Platform breakdown */}
          <div className="px-6 py-5">
            <p className="mb-4 text-[10px] font-semibold tracking-wider text-ink-muted">PLATFORM BREAKDOWN</p>
            <div className="space-y-3">
              {[
                { name: "ChatGPT", score: 86, domain: "chatgpt.com" },
                { name: "Claude", score: 82, domain: "claude.ai" },
                { name: "Perplexity", score: 91, domain: "perplexity.ai" },
                { name: "Gemini", score: 78, domain: "gemini.google.com" },
                { name: "AI Overviews", score: 71, domain: "google.com" },
              ].map((p) => (
                <div key={p.name} className="flex items-center gap-3">
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${p.domain}&sz=64`}
                    alt=""
                    aria-hidden="true"
                    width={20}
                    height={20}
                    loading="lazy"
                    className="h-5 w-5 rounded"
                  />
                  <span className="w-24 text-sm text-ink-body">{p.name}</span>
                  <div className="flex-1">
                    <div className="h-2.5 overflow-hidden rounded-full bg-hairline">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${p.score}%` }} />
                    </div>
                  </div>
                  <span className="w-8 text-right text-sm font-medium text-ink-display">{p.score}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Competitor rankings */}
          <div className="border-t border-hairline px-6 py-5">
            <p className="mb-4 text-[10px] font-semibold tracking-wider text-ink-muted">COMPETITOR RANKINGS</p>
            <div className="space-y-2">
              {[
                { rank: 1, name: "Rival Agency A", score: 87, delta: "+0.2" },
                { rank: 2, name: "Rival Agency B", score: 85, delta: "+1.4" },
                { rank: 3, name: "Acme Consulting", score: 84, delta: "+3.3", highlight: true },
                { rank: 4, name: "Rival Agency C", score: 72, delta: "-0.4" },
                { rank: 5, name: "Rival Agency D", score: 65, delta: "+1.1" },
              ].map((c) => (
                <div
                  key={c.rank}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${c.highlight ? "bg-accent/10" : ""}`}
                >
                  <span className="w-6 font-bold text-ink-muted">#{c.rank}</span>
                  <span className={`flex-1 ${c.highlight ? "font-semibold text-accent" : "text-ink-body"}`}>{c.name}</span>
                  <span className="font-medium text-ink-display">{c.score}</span>
                  <span className={`w-12 text-right text-xs ${c.delta.startsWith("+") ? "text-green-500" : "text-red-400"}`}>
                    {c.delta}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* How it works */}
      <Section className="bg-surface" containerClassName="max-w-4xl">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="display-2 mt-4">From prompts to portal to growth</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.step} className="rounded-xl border border-hairline bg-page p-6">
              <p className="text-xs font-semibold tracking-wider text-accent">{s.step}</p>
              <h3 className="mt-2">{s.title}</h3>
              <p className="mt-2 text-ink-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* The shift */}
      <Section containerClassName="max-w-3xl">
        <Eyebrow>The shift</Eyebrow>
        <h2 className="display-2 mt-4">Search is being replaced by recommendations</h2>
        <p className="mt-5 text-lg text-ink-body">
          People used to Google a category and scan ten blue links. Now they ask an
          AI and get a short list of names — or one answer. Being on page one of
          Google means nothing if you're absent from the answer the AI gives. This
          is a new surface, with new rules, and most brands have no idea where they
          stand on it.
        </p>
      </Section>

      {/* What you get */}
      <Section containerClassName="max-w-4xl">
        <Eyebrow>What you get</Eyebrow>
        <h2 className="display-2 mt-4">Measure it. Then move it.</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {TIERS.map((t) => (
            <div key={t.name} className="rounded-xl border border-hairline bg-surface p-6">
              <h3>{t.name}</h3>
              <p className="mt-2 text-ink-muted">{t.line}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Why FullCast */}
      <Section className="bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>Why FullCast</Eyebrow>
        <h2 className="display-2 mt-4">A methodology, not just a metric</h2>
        <p className="mt-5 text-lg text-ink-body">
          Most "AI visibility" tools only hand you a number. FullCast hands you a
          system: a methodology built from a decade of authority work for
          expert-led brands, and a live dashboard you log into to watch the score
          move. The data is yours. The methodology is ours. The work behind the
          numbers is what compounds.
        </p>
        <div className="mt-8">
          <Button asChild size="lg">
            <a href="#waitlist">Join the waitlist</a>
          </Button>
        </div>
      </Section>
    </MarketingLayout>
  )
}
